import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GeneriqueData } from '../models/models';
import api from './api';
import { GeneriqueDataType } from '../constants/enums';

interface Filter {
  type: GeneriqueDataType;
  departement_id?: number | null;
  limit: number;
  offset: number;
}

interface GeneriqueDataState {
  filter: Filter;
  generiqueData: GeneriqueData[];
  caisseData: GeneriqueData[];
  depotData: GeneriqueData[];
  modeReglementData: GeneriqueData[];
  natureReglementData: GeneriqueData[];
  natureTacheData: GeneriqueData[];
  // Données génériques indexées par type — utilisé notamment par SelectCategorie
  // (générique, sans slot dédié par type catégorie).
  byType: Partial<Record<GeneriqueDataType, GeneriqueData[]>>;
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedGeneriqueData: GeneriqueData | null;
}

const initialState: GeneriqueDataState = {
  filter: {
    type: GeneriqueDataType.DEPOT,
    limit: 1000,
    offset: 0,
  },
  generiqueData: [],
  caisseData: [],
  depotData: [],
  modeReglementData: [],
  natureReglementData: [],
  natureTacheData: [],
  byType: {},
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedGeneriqueData: null,
};

export const fetchGeneriqueDataByType = createAsyncThunk<
  {
    data: GeneriqueData[];
    total: number;
    totalPages: number;
    currentPage: number;
    type: GeneriqueDataType;
  },
  {
    type: GeneriqueDataType;
    departement_id?: number | null;
  },
  { rejectValue: string }
>('generiqueData/fetchByType', async ({ type, departement_id }, thunkAPI) => {
  try {
    const response = await api.get('/generiqueData', {
      params: { type, departement_id: departement_id ?? undefined, limit: 1000, offset: 0 },
    });
    return { ...response.data, type };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || `Failed to load data for type ${type}`
    );
  }
});

export const createGeneriqueData = createAsyncThunk<
  GeneriqueData,
  GeneriqueData,
  { rejectValue: string }
>('generiqueData/createOrUpdateGeneriqueData', async (data, thunkAPI) => {
  try {
    const response = await api.post('/generiqueData', data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
  }
});

export const deleteGeneriqueData = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('generiqueData/deleteGeneriqueData', async (id, thunkAPI) => {
  try {
    await api.delete(`/generiqueData`, {
      params: { id },
    });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
  }
});


const generiqueDataSlice = createSlice({
  name: 'generiqueData',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<Filter>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedGeneriqueData: (state, action: PayloadAction<GeneriqueData | null>) => {
      state.selectedGeneriqueData = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearAllData: (state) => {
      state.generiqueData = [];
      state.caisseData = [];
      state.depotData = [];
      state.modeReglementData = [];
      state.natureReglementData = [];
      state.natureTacheData = [];
      state.byType = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeneriqueDataByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGeneriqueDataByType.fulfilled, (state, action) => {
        state.loading = false;
        const { type, data, total, totalPages, currentPage } = action.payload;
        state.generiqueData = data;
        state.byType[type] = data;
        switch (type) {
          case GeneriqueDataType.CAISSE:
            state.caisseData = data;
            break;
          case GeneriqueDataType.DEPOT:
            state.depotData = data;
            break;
          case GeneriqueDataType.MODE_REGLEMENT:
            state.modeReglementData = data;
            break;
          case GeneriqueDataType.NATURE_REGLEMENT:
            state.natureReglementData = data;
            break;
          case GeneriqueDataType.NATURE_TACHE:
            state.natureTacheData = data;
            break;
        }
        state.total = total;
        state.totalPages = totalPages;
        state.currentPage = currentPage;
      })
      .addCase(fetchGeneriqueDataByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createGeneriqueData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createGeneriqueData.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Creation/Update successful!';
      })
      .addCase(createGeneriqueData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteGeneriqueData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteGeneriqueData.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Deletion successful!';
      })
      .addCase(deleteGeneriqueData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilter,
  setCurrentPage,
  setSelectedGeneriqueData,
  clearMessages,
  clearAllData,
} = generiqueDataSlice.actions;

export default generiqueDataSlice.reducer;
